import React from "react";
import { notification } from "antd";

import {
  DownloadCallbacks,
  DownloadResponse,
  DownloadStatus,
  PollingOptions,
} from "@/shared/services/types";

class DownloadPollingService {
  private pollIntervals: Record<string, number> = {};
  private timeouts: Record<string, number> = {};
  private notificationKeys: Record<string, string> = {};
  private notificationConfigCreators: Record<
    string,
    (progress?: number) => { message: string; description: React.ReactNode }
  > = {};

  async initiateDownload(
    id: string,
    downloadQueryTrigger: any,
    options: PollingOptions,
    callbacks: DownloadCallbacks,
    notificationConfig?:
      | {
          message: string;
          description: React.ReactNode;
        }
      | ((progress?: number) => {
          message: string;
          description: React.ReactNode;
        }),
  ): Promise<void> {
    try {
      const response = await downloadQueryTrigger(id).unwrap();

      if (response.status === DownloadStatus.FINISHED && response.link) {
        if (options.autoDownload !== false) {
          this.downloadFile(response.link, response.name);
        }
        callbacks.onSuccess(response);
      } else if (
        response.status === DownloadStatus.STARTED ||
        response.status === DownloadStatus.RUNNING
      ) {
        // Start polling
        this.startPolling(
          id,
          downloadQueryTrigger,
          options,
          callbacks,
          notificationConfig,
          response.progress,
        );
      } else if (response.status === DownloadStatus.FAILED) {
        callbacks.onError();
      }
    } catch {
      callbacks.onError();
    }
  }

  private startPolling(
    id: string,
    downloadQueryTrigger: any,
    options: PollingOptions,
    callbacks: DownloadCallbacks,
    notificationConfig?:
      | {
          message: string;
          description: React.ReactNode;
        }
      | ((progress?: number) => {
          message: string;
          description: React.ReactNode;
        }),
    initialProgress?: number,
  ): void {
    this.stopPolling(id);

    const { pollInterval, timeout } = options;
    const uniqueNotificationKey = `download-${id}-${Date.now()}`;
    this.notificationKeys[id] = uniqueNotificationKey;

    if (notificationConfig) {
      if (typeof notificationConfig === "function") {
        this.notificationConfigCreators[id] = notificationConfig;
      } else {
        this.notificationConfigCreators[id] = () => notificationConfig;
      }

      const initialConfig =
        this.notificationConfigCreators[id](initialProgress);
      notification.open({
        type: "info",
        message: initialConfig.message,
        description: initialConfig.description,
        duration: 0,
        closable: false,
        key: uniqueNotificationKey,
        placement: "bottomRight",
      });
    }

    this.pollIntervals[id] = window.setInterval(async () => {
      try {
        const response = await downloadQueryTrigger(id).unwrap();

        if (
          response.status === DownloadStatus.STARTED ||
          response.status === DownloadStatus.RUNNING
        ) {
          if (
            response.progress !== undefined &&
            this.notificationConfigCreators[id] &&
            this.notificationKeys[id]
          ) {
            const config = this.notificationConfigCreators[id](
              response.progress,
            );
            notification.open({
              type: "info",
              message: config.message,
              description: config.description,
              duration: 0,
              closable: false,
              key: this.notificationKeys[id],
              placement: "bottomRight",
            });
          }
        }

        if (response.status === DownloadStatus.FINISHED && response.link) {
          this.handleSuccess(
            id,
            response,
            uniqueNotificationKey,
            callbacks,
            options.autoDownload !== false,
          );
        } else if (response.status === DownloadStatus.FAILED) {
          this.handleError(id, uniqueNotificationKey, callbacks);
        }
        // Continue polling for STARTED/RUNNING
      } catch {
        this.handleError(id, uniqueNotificationKey, callbacks);
      }
    }, pollInterval);

    // Set timeout
    this.timeouts[id] = window.setTimeout(() => {
      this.handleTimeout(id, uniqueNotificationKey, callbacks);
    }, timeout);
  }

  private handleSuccess(
    id: string,
    response: DownloadResponse,
    notificationKey: string,
    callbacks: DownloadCallbacks,
    autoDownload: boolean = true,
  ): void {
    this.cleanup(id, notificationKey);
    if (autoDownload) {
      this.downloadFile(response.link as string, response.name);
    }
    callbacks.onSuccess(response);
    callbacks.onCleanup(id, notificationKey);
  }

  private handleError(
    id: string,
    notificationKey: string,
    callbacks: DownloadCallbacks,
  ): void {
    this.cleanup(id, notificationKey);
    callbacks.onError();
    callbacks.onCleanup(id, notificationKey);
  }

  private handleTimeout(
    id: string,
    notificationKey: string,
    callbacks: DownloadCallbacks,
  ): void {
    this.cleanup(id, notificationKey);
    callbacks.onTimeout();
    callbacks.onCleanup(id, notificationKey);
  }

  private cleanup(id: string, notificationKey: string): void {
    if (this.pollIntervals[id]) {
      clearInterval(this.pollIntervals[id]);
      delete this.pollIntervals[id];
    }

    if (this.timeouts[id]) {
      clearTimeout(this.timeouts[id]);
      delete this.timeouts[id];
    }

    delete this.notificationKeys[id];
    delete this.notificationConfigCreators[id];
    notification.destroy(notificationKey);
  }

  private isSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    return (
      ua.includes("safari") &&
      !ua.includes("chrome") &&
      !ua.includes("chromium")
    );
  };

  private downloadFile(url: string, filename: string): void {
    if (this.isSafari()) {
      window.open(url, "_blank");
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "download";
      link.target = "_blank";
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  stopPolling(id: string): void {
    if (this.pollIntervals[id]) {
      clearInterval(this.pollIntervals[id]);
      delete this.pollIntervals[id];
    }

    if (this.timeouts[id]) {
      clearTimeout(this.timeouts[id]);
      delete this.timeouts[id];
    }

    if (this.notificationKeys[id]) {
      notification.destroy(this.notificationKeys[id]);
      delete this.notificationKeys[id];
    }

    delete this.notificationConfigCreators[id];
  }
}

export const downloadPollingService = new DownloadPollingService();
